// Eskom PARCC — regional date/time formatting.
//
// One place that answers "how does this user want a timestamp rendered?", so the
// Settings → Regional & format preference actually reaches the screens. Before
// this, `timeZone: 'Africa/Johannesburg'` and `toLocaleDateString('en-ZA')` were
// hard-coded at each call site.
//
// DELIBERATELY NOT CURRENCY. `lib/money.ts` owns the Rand contract — `formatRand`
// and its exact inverse `parseRand` are relied on by the Excel exports, the bulk
// importer and the value parser, so a per-user locale must never touch it. This
// module governs DATES and plain number grouping only.
import { DEFAULT_UI_PREFS } from './uiPrefs';

/** Read the active preference off the DOM/storage without needing the provider —
 *  these helpers are called from pure render paths and export builders alike. */
function activePrefs(): { timeZone: string; numberFormat: string } {
  if (typeof localStorage === 'undefined') {
    return { timeZone: DEFAULT_UI_PREFS.timeZone, numberFormat: DEFAULT_UI_PREFS.numberFormat };
  }
  try {
    const raw: unknown = JSON.parse(localStorage.getItem('parcc.prefs') || '{}');
    const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
      timeZone: typeof p.timeZone === 'string' ? p.timeZone : DEFAULT_UI_PREFS.timeZone,
      numberFormat:
        typeof p.numberFormat === 'string' ? p.numberFormat : DEFAULT_UI_PREFS.numberFormat,
    };
  } catch {
    return { timeZone: DEFAULT_UI_PREFS.timeZone, numberFormat: DEFAULT_UI_PREFS.numberFormat };
  }
}

/** Coerce anything date-ish to a Date, or null when it is not a real date. */
function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date only, in the reader's locale + zone. Empty string for a non-date. */
export function formatDate(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  const { timeZone, numberFormat } = activePrefs();
  return d.toLocaleDateString(numberFormat, { timeZone, ...options });
}

/** Date + time, in the reader's locale + zone. Empty string for a non-date. */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = {},
): string {
  const d = toDate(value);
  if (!d) return '';
  const { timeZone, numberFormat } = activePrefs();
  return d.toLocaleString(numberFormat, { timeZone, ...options });
}

/** A plain (non-currency) number with the reader's grouping separators. */
export function formatNumber(
  value: number | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return value.toLocaleString(activePrefs().numberFormat, options);
}

/** The reader's chosen zone — for the few places that need it directly (a
 *  snapshot table's "(SAST)" column heading, say). */
export function activeTimeZone(): string {
  return activePrefs().timeZone;
}

/** A short label for the active zone, e.g. `SAST`. Falls back to the IANA name. */
export function timeZoneAbbreviation(date: Date = new Date()): string {
  const zone = activeTimeZone();
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? zone;
  } catch {
    return zone;
  }
}
